import {
  YES_NO,
  applicantSection,
  businessSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
} from "modules/ebiForms/common";
import { businessRepresentationSection } from "modules/ebiForms/definitions/sharedMunicipalServices";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B7. Wniosek o wydanie zezwolenia na sprzedaż napojów alkoholowych — art. 18 (treść wniosku: ust. 5, załączniki:
 * ust. 6) i art. 18¹ (jednorazowe — ust. 1, catering — ust. 4) ustawy z 26.10.1982 r. o wychowaniu w trzeźwości
 * i przeciwdziałaniu alkoholizmowi; opłaty — art. 11¹. Warianty z katalogu: stałe / jednorazowe / catering.
 * Wzór ogólnopolski przewiduje ustawa (art. 18 ust. 6a) — realizacją są e-usługi biznes.gov.pl (ou280, ou286, ou296,
 * opisy sprawdzone 2026-10-22); układ pól jak wzór Gminy Zwoleń pobrany 2026-10-22 (pkt 1–7 = art. 18 ust. 5,
 * 3 załączniki z ust. 6) —
 * https://bip.zwolen.pl/upload/Wniosek%20o%20wydanie%20zezwolenia%20na%20sprzeda%C5%BC%20napoj%C3%B3w%20alkoholowych.pdf.
 * Dane imprezy przy zezwoleniu jednorazowym, kopia zezwolenia stałego i zgoda organizatora — praktyka urzędów.
 * Opłatę za korzystanie z zezwolenia stałego i cateringowego wnosi się przed wydaniem zezwolenia (kwota proporcjonalna
 * do okresu w roku), więc jej dowód nie jest wymagany przy wniosku; przy jednorazowym kwota jest stała (1/12 rocznej).
 */
const PERMANENT: EbiCondition = { field: "rodzajZezwolenia", equals: "stale" };
const ONE_OFF: EbiCondition = { field: "rodzajZezwolenia", equals: "jednorazowe" };
const CATERING: EbiCondition = { field: "rodzajZezwolenia", equals: "catering" };
const ONE_OFF_BUSINESS: EbiCondition = { all: [ONE_OFF, { field: "podmiotJednorazowe", equals: "przedsiebiorca" }] };

const B7: IEbiFormDefinition = {
  code: "B7",
  version: 1,
  title: "Wniosek o wydanie zezwolenia na sprzedaż napojów alkoholowych",
  category: EEbiFormCategory.Permits,
  office: EEbiFormOffice.Municipality,
  description: "Zezwolenie na sprzedaż alkoholu w sklepie lub lokalu gastronomicznym, jednorazowe (impreza) albo na sprzedaż podczas przyjęć (catering).",
  keywords: ["alkohol", "zezwolenie", "koncesja", "sprzedaż alkoholu", "piwo", "gastronomia", "catering", "impreza"],
  organ: "Wójt / Burmistrz / Prezydent Miasta właściwy ze względu na lokalizację punktu sprzedaży",
  legalBasis:
    "art. 18 i art. 18¹ ustawy z dnia 26 października 1982 r. o wychowaniu w trzeźwości i przeciwdziałaniu alkoholizmowi",
  request:
    "Wnoszę o wydanie zezwolenia na sprzedaż napojów alkoholowych w rodzaju i zakresie wskazanym poniżej.",
  feeLabel: "wg stawek",
  fee: "Opłata za korzystanie z zezwolenia (art. 11¹): w roku wydania 525 zł (do 4,5% i piwo), 525 zł (4,5–18%), 2100 zł (pow. 18%) — proporcjonalnie do okresu ważności, wnoszona przed wydaniem zezwolenia; jednorazowe — 43,75 zł / 43,75 zł / 175 zł. Zezwolenie nie podlega opłacie skarbowej; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do 2 miesięcy (KPA).",
  variantField: "rodzajZezwolenia",
  variants: {
    stale: { title: "Wniosek o wydanie zezwolenia na sprzedaż napojów alkoholowych" },
    jednorazowe: { title: "Wniosek o wydanie jednorazowego zezwolenia na sprzedaż napojów alkoholowych" },
    catering: { title: "Wniosek o wydanie zezwolenia na sprzedaż napojów alkoholowych podczas przyjęć (catering)" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "1. Oznaczenie rodzaju zezwolenia",
      fields: [
        {
          code: "rodzajZezwolenia",
          type: "radio",
          label: "Wnoszę o wydanie",
          required: true,
          defaultValue: "stale",
          options: [
            { value: "stale", label: "zezwolenia na sprzedaż napojów alkoholowych w punkcie sprzedaży (art. 18)" },
            {
              value: "jednorazowe",
              label: "jednorazowego zezwolenia — sprzedaż podczas imprezy, na okres do 2 dni (art. 18¹ ust. 1)",
            },
            {
              value: "catering",
              label: "zezwolenia na sprzedaż podczas przyjęć organizowanych w czasie i miejscu wskazanym przez klienta — catering (art. 18¹ ust. 4)",
            },
          ],
        },
        {
          code: "rodzajeNapojow",
          type: "checkboxes",
          label: "Rodzaj napojów alkoholowych",
          required: true,
          options: [
            { value: "A", label: "A — do 4,5% zawartości alkoholu oraz piwo" },
            { value: "B", label: "B — powyżej 4,5% do 18% zawartości alkoholu (z wyjątkiem piwa)" },
            { value: "C", label: "C — powyżej 18% zawartości alkoholu" },
          ],
        },
        {
          code: "miejsceSpozycia",
          type: "radio",
          label: "Napoje przeznaczone do spożycia",
          required: true,
          visibleWhen: { any: [PERMANENT, ONE_OFF] },
          options: [
            { value: "wMiejscu", label: "w miejscu sprzedaży (gastronomia)" },
            { value: "pozaMiejscem", label: "poza miejscem sprzedaży (sklep)" },
          ],
        },
        {
          code: "okresWaznosci",
          type: "text",
          label: "Wnioskowany okres ważności zezwolenia",
          visibleWhen: { any: [PERMANENT, CATERING] },
          maxLength: 100,
          placeholder: "np. od 1 stycznia 2027 r. do 31 grudnia 2030 r.",
          hint: "Zezwolenie w punkcie sprzedaży wydaje się na co najmniej 4 lata (gastronomia) albo 2 lata (handel detaliczny); zezwolenie cateringowe — do 2 lat.",
        },
      ],
    },
    applicantSection("Wnioskodawca (osoba składająca wniosek)"),
    businessRepresentationSection(),
    {
      code: "podmiot",
      title: "Uprawnienie do zezwolenia jednorazowego",
      visibleWhen: ONE_OFF,
      fields: [
        {
          code: "podmiotJednorazowe",
          type: "radio",
          label: "Wnioskodawcą jest",
          required: true,
          options: [
            { value: "przedsiebiorca", label: "przedsiębiorca posiadający zezwolenie na sprzedaż napojów alkoholowych" },
            { value: "osp", label: "jednostka Ochotniczej Straży Pożarnej" },
          ],
        },
        {
          code: "numerZezwolenia",
          type: "text",
          label: "Numer posiadanego zezwolenia na sprzedaż napojów alkoholowych i organ, który je wydał",
          required: true,
          visibleWhen: ONE_OFF_BUSINESS,
          maxLength: 200,
        },
      ],
    },
    businessSection("2–3. Oznaczenie przedsiębiorcy (siedziba i adres, KRS, NIP)"),
    {
      code: "dzialalnosc",
      title: "4–6. Działalność i punkty sprzedaży",
      fields: [
        {
          code: "przedmiotDzialalnosci",
          type: "textarea",
          label: "Przedmiot działalności gospodarczej",
          requiredWhen: { not: { all: [ONE_OFF, { field: "podmiotJednorazowe", equals: "osp" }] } },
          maxLength: 1000,
        },
        {
          code: "adresPunktuSprzedazy",
          type: "text",
          label: "Adres punktu sprzedaży",
          required: true,
          visibleWhen: PERMANENT,
          maxLength: 300,
        },
        {
          code: "budynekWielorodzinny",
          type: "radio",
          label: "Czy punkt sprzedaży znajduje się w budynku mieszkalnym wielorodzinnym?",
          required: true,
          visibleWhen: PERMANENT,
          options: YES_NO,
        },
        {
          code: "adresMagazynu",
          type: "text",
          label: "Adres punktu składowania napojów alkoholowych (magazynu dystrybucyjnego)",
          required: true,
          maxLength: 300,
          hint: "Może to być adres punktu sprzedaży.",
        },
      ],
    },
    {
      code: "impreza",
      title: "Impreza, podczas której będzie prowadzona sprzedaż",
      visibleWhen: ONE_OFF,
      fields: [
        { code: "nazwaImprezy", type: "text", label: "Nazwa (rodzaj) imprezy", required: true, maxLength: 300 },
        { code: "miejsceImprezy", type: "text", label: "Miejsce imprezy (adres, teren)", required: true, maxLength: 300 },
        { code: "dataOd", type: "date", label: "Sprzedaż od dnia", required: true, width: "half" },
        { code: "dataDo", type: "date", label: "Sprzedaż do dnia", width: "half", hint: "Najwyżej 2 dni." },
        { code: "godziny", type: "text", label: "Godziny sprzedaży", width: "half", maxLength: 100, placeholder: "np. 16:00–24:00" },
        { code: "organizator", type: "text", label: "Organizator imprezy", width: "half", maxLength: 300 },
      ],
    },
    {
      code: "oplata",
      title: "Opłata za korzystanie z zezwolenia",
      fields: [
        {
          code: "oplataJednorazoweA",
          type: "fee",
          label: "Opłata — jednorazowe zezwolenie A (do 4,5% i piwo)",
          feeAmount: 43.75,
          visibleWhen: { all: [ONE_OFF, { field: "rodzajeNapojow", includes: "A" }] },
        },
        {
          code: "oplataJednorazoweB",
          type: "fee",
          label: "Opłata — jednorazowe zezwolenie B (4,5–18%)",
          feeAmount: 43.75,
          visibleWhen: { all: [ONE_OFF, { field: "rodzajeNapojow", includes: "B" }] },
        },
        {
          code: "oplataJednorazoweC",
          type: "fee",
          label: "Opłata — jednorazowe zezwolenie C (powyżej 18%)",
          feeAmount: 175,
          visibleWhen: { all: [ONE_OFF, { field: "rodzajeNapojow", includes: "C" }] },
        },
        {
          ...feeInfo(
            "infoOplataJednorazowe",
            "Jednorazowe zezwolenie: 1/12 rocznej opłaty za każdy rodzaj napojów (art. 18¹ ust. 3). Dołącz potwierdzenie przelewu na rachunek gminy.",
          ),
          visibleWhen: ONE_OFF,
        },
        {
          ...feeInfo(
            "infoOplataRoczna",
            "Opłatę za korzystanie z zezwolenia wnosi się na rachunek gminy przed jego wydaniem — w roku wydania proporcjonalnie do okresu ważności (525 zł / 525 zł / 2100 zł za pełny rok); urząd poinformuje o kwocie. W kolejnych latach opłata zależy od wartości sprzedaży, a oświadczenie o wartości sprzedaży za poprzedni rok składa się do 31 stycznia — jego niezłożenie powoduje wygaśnięcie zezwolenia (art. 11¹ i art. 18 ustawy).",
          ),
          visibleWhen: { any: [PERMANENT, CATERING] },
        },
      ],
    },
    replySection("Sposób odbioru zezwolenia"),
  ],
  attachments: [
    {
      code: "tytulLokalu",
      label: "Dokument potwierdzający tytuł prawny do lokalu stanowiącego punkt sprzedaży napojów alkoholowych",
      visibleWhen: PERMANENT,
      required: true,
    },
    {
      code: "decyzjaSanepid",
      label: "Decyzja państwowego inspektora sanitarnego o zatwierdzeniu zakładu (art. 65 ust. 1 pkt 2 ustawy o bezpieczeństwie żywności i żywienia)",
      visibleWhen: PERMANENT,
      required: true,
    },
    {
      code: "zgodaZarzadcy",
      label: "Pisemna zgoda właściciela, użytkownika, zarządcy lub administratora budynku",
      hint: "Punkt sprzedaży w budynku mieszkalnym wielorodzinnym (art. 18 ust. 6 pkt 3).",
      visibleWhen: { all: [PERMANENT, { field: "budynekWielorodzinny", equals: "tak" }] },
      required: true,
    },
    {
      code: "kopiaZezwolenia",
      label: "Kopia posiadanego zezwolenia na sprzedaż napojów alkoholowych",
      hint: "Nieobowiązkowo, gdy podano numer zezwolenia (praktyka urzędów).",
      visibleWhen: ONE_OFF_BUSINESS,
    },
    {
      code: "zgodaOrganizatora",
      label: "Zgoda organizatora imprezy albo właściciela (zarządcy) terenu",
      hint: "Urzędy zwykle o nią proszą (praktyka urzędów).",
      visibleWhen: ONE_OFF,
    },
    feeProofAttachment("Dowód wniesienia opłaty za korzystanie z zezwolenia", {
      required: false,
      requiredWhen: ONE_OFF,
      hint: "Przy zezwoleniu jednorazowym — wymagany. Przy pozostałych opłatę wnosi się przed wydaniem zezwolenia (możesz dołączyć dowód, jeśli już zapłaciłaś/zapłaciłeś).",
    }),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default B7;
